"use client";

import type { RichTextComponent } from "../../types";
import { sanitizeRichTextHtml } from "../../utils/richTextHtml";
import { useRendererRuntime } from "../../runtime/RendererRuntime";

/**
 * RichText block — renders legacy editorial body HTML (institutional/basic
 * pages, governor bios, banknote pages) faithfully: headings, lists, links,
 * images, quotes and tables all survive, unlike `TextBlock` whose paragraph
 * sanitiser strips them.
 *
 * The body HTML is run through `sanitizeRichTextHtml` (the ONLY path it reaches
 * the DOM) and injected via `dangerouslySetInnerHTML`. Inline `<img>` tags are
 * not React components, so their bucket-relative `src` keys are resolved here
 * through the runtime `assetResolver` — the same env-aware resolution
 * `DataRecordPdfPreview` applies to PDFs. This is a **client component** so it
 * can read the runtime context; new-site typography/colours (headings primary
 * blue `#00B862`, body navy `#121212`, links `#00B862`) are applied via the
 * shared `rich-paragraph-block` styling and a scoped `rich-text-block` class.
 */

/** True when a src is already a browser-loadable absolute URL. */
function isAbsoluteUrl(src: string): boolean {
  return /^(?:https?:)?\/\//i.test(src) || /^(?:data|blob):/i.test(src);
}

/**
 * Rewrites inline `<img src="...">` keys through the asset resolver so legacy
 * storage keys become environment-aware absolute URLs. Already-absolute URLs
 * are still passed to the resolver (it passes them through unchanged).
 */
function resolveInlineImages(
  html: string,
  resolver?: (key: string) => string,
): string {
  if (!resolver) return html;
  return html.replace(
    /(<img\b[^>]*?\bsrc=")([^"]*)(")/gi,
    (_match, pre: string, src: string, post: string) => {
      const trimmed = src.trim();
      if (!trimmed) return `${pre}${src}${post}`;
      const resolved = resolver(trimmed);
      return `${pre}${resolved}${post}`;
    },
  );
}

/** RichText renderer for migrated editorial body HTML. */
export function RichTextRenderer({ component }: { component: RichTextComponent }) {
  const { assetResolver } = useRendererRuntime();
  const raw = component.content?.html ?? "";
  const sanitized = sanitizeRichTextHtml(raw);
  const html = resolveInlineImages(sanitized, assetResolver);

  if (!html) return null;

  return (
    <section
      className="rich-text-block rich-paragraph-block text-base xl:text-lg text-[#121212] [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-[#00B862] [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-[#00B862] [&_h4]:mt-5 [&_h4]:mb-2 [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-[#00B862] [&_p]:mb-4 [&_a]:text-[#00B862] [&_a]:underline [&_a:hover]:text-[#121212] [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-1 [&_blockquote]:my-5 [&_blockquote]:border-l-4 [&_blockquote]:border-[#00B862] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-[#121212] [&_table]:my-5 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-slate-200 [&_th]:bg-[#EEF1F0] [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_td]:border [&_td]:border-slate-200 [&_td]:px-3 [&_td]:py-2 [&_img]:my-4 [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded [&_hr]:my-6 [&_hr]:border-slate-200"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
